import { WorkCase } from '@/data/types';

const developmentOfRobinwichmannCom: WorkCase = {
  id: "robinwichmann-com",
  slug: "development-of-robinwichmann.com",
  title: "Entwicklung von robinwichmann.com",
  description: "Konzeption und Entwicklung meiner persönlichen Portfolio-Website mit modernen Web-Technologien und optimaler Performance.",
  tags: ["Next.js", "React", "TypeScript", "TailwindCSS", "Framer Motion", "Cloudflare Pages", "Responsive Design", "SSR"],
  image: "/assets/img/work/dev_of_robinwichmann_com.webp",
  articleType: "DEVLOG",
  detailContent: {
    subtitle: "Konzeption und Entwicklung einer modernen Portfolio-Website mit Next.js und React",
    challenge: [
      {
        text: "Als Webentwickler wollte ich eine persönliche Portfolio-Website schaffen, die nicht nur meine Fähigkeiten zeigt, sondern selbst ein Beispiel für mein Können ist – modern, interaktiv und technisch sauber umgesetzt."
      },
      {
        text: "Die größte Herausforderung bestand darin, all meine Anforderungen unter einen Hut zu bringen:"
      },
      {
        listing: [
          "Eine moderne, ansprechende Benutzeroberfläche mit intuitivem und lebendigem Design, das zum Entdecken und Ausprobieren einlädt",
          "Schnelle Ladezeiten und optimale Performance",
          "Responsive Design für alle Geräte",
          "SEO-Optimierung",
          "Einfache Wartbarkeit und Erweiterbarkeit",
          "Barrierefreiheit nach WCAG-Standards"
        ]
      }
    ],
    solution: [
      {
        text: "Ich entschied mich für einen modernen Tech-Stack, der Leistung, Entwicklerfreundlichkeit und Zukunftssicherheit bietet:"
      },
      {
        subheadline: "1. Frontend-Framework",
        text: "Ich wählte Next.js 15 als Framework, das Server-Side Rendering (SSR), statische Seitengenerierung und optimierte Performance bietet. Die Kombination mit React 19 ermöglichte eine effiziente Komponenten-basierte Entwicklung."
      },
      {
        subheadline: "2. Sprache und Typisierung",
        text: "Mit TypeScript konnte ich eine robuste Codebase erstellen, die Typfehler frühzeitig erkennt und die Wartbarkeit verbessert. Die strikte Typisierung sorgt für konsistente Datenstrukturen und erleichtert zukünftige Erweiterungen."
      },
      {
        subheadline: "3. Styling und Animationen",
        text: "TailwindCSS 4 revolutionierte mein Styling-System durch den Einsatz des OKLCH-Farbraums für lebhaftere Farbdarstellung und präzisere Farbverläufe. Die CSS-First-Konfiguration ermöglichte mir, auf eine separate tailwind.config.js zu verzichten und stattdessen Design-Tokens direkt über CSS-Variablen und die @theme-Direktive zu definieren. Besonders für die komplexen Hintergrundanimationen nutzte ich CSS @property-Definitionen in Kombination mit CSS-Variablen, die sich über die Seiten hinweg sanft verändern. Für interaktive Komponenten wie die Bildergalerie setzte ich auf Framer Motion, das mit seiner AnimatePresence-Komponente und gerichteten Transitionen für flüssige Ein- und Ausblendeffekte sorgt, ohne die Performance zu beeinträchtigen."
      },
      {
        subheadline: "4. Deployment und Hosting",
        text: "Die Website wird auf Cloudflare Pages gehostet, was eine globale Content Delivery Network (CDN) Verteilung, automatisches SSL und CI/CD-Integration bietet. Die Seite lädt dadurch weltweit schnell und ist sicher erreichbar."
      },
      {
        subheadline: "5. Modulare Datenstruktur",
        text: "Ich implementierte eine modulare Datenstruktur mit TypeScript-Interfaces, die alle Inhalte der Website in strukturierten, typisierten Formaten organisiert. Dies erleichtert die Wartung und macht Content-Updates einfach."
      },
      {
        subheadline: "6. Testing und Qualitätssicherung",
        text: "Mit Cypress implementierte ich End-to-End-Tests, die kritische Benutzerpfade und Funktionalitäten automatisch überprüfen. Dies stellt sicher, dass neue Features oder Änderungen keine Regressionen verursachen."
      }
    ],
    results: [
      {
        text: "Die Entwicklung resultierte in einer modernen, performanten Portfolio-Website:"
      },
      {
        listing: [
          "Lighthouse-Score von über 95 in allen Kategorien (Performance, Accessibility, Best Practices, SEO)",
          "Schnelle Ladezeiten unter 2 Sekunden auch auf mobilen Netzwerken",
          "Responsive Design, das auf allen Geräten optimal funktioniert",
          "Automatisierte Deployment-Pipeline mit Cloudflare Pages",
          "Umfassende Teststrategie mit Cypress für E2E-Tests",
          "Modulare, gut strukturierte Codebasis für einfache Erweiterungen",
          "WCAG-konforme Barrierefreiheit für eine inklusive Nutzererfahrung",
          "Optimierte Farbpalette mit OKLCH-Farbraum für ein lebendigeres Erscheinungsbild und bessere Farbverläufe, besonders sichtbar bei den seitenspezifischen Hintergrundanimationen"
        ]
      },
      {
        text: "Die Website dient nicht nur als Portfolio, sondern auch als Experimentierfeld für neue Web-Technologien und Best Practices. Durch die gewählte Architektur kann ich neue Features schnell implementieren und testen, während ich gleichzeitig eine optimale Benutzererfahrung biete."
      }
    ],
    technologies: [
      "Next.js 15",
      "React 19",
      "TypeScript 5",
      "TailwindCSS 4",
      "Framer Motion",
      "Cloudflare Pages",
      "SASS",
      "Cypress",
      "ESLint",
      "Vercel",
      "Git/GitHub"
    ],
    codeExamples: [
      {
        language: "typescript",
        code: `// Animation mit Framer Motion für Komponenten-Übergänge
export const fadeInAnimationVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.05 * index,
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  }),
};`,
        description: "Animation mit Framer Motion für sanfte Einblend-Effekte mit gestaffelter Verzögerung basierend auf dem Index des Elements."
      },
      {
        language: "scss",
        code: `@theme {
  // Basisfarben für die Website mit OKLCH-Farbraum
  --colorNeutralSolid: oklch(0.15 0.05 270);   // #1e0b32
  --colorNeutralLight: oklch(0.85 0.12 270);   // #ff88ff
  --colorHomeSolid: oklch(0.12 0.05 270);      // #0c0b32
  --colorHomeLight: oklch(0.88 0.1 195);       // #88ffff
  --colorWorkSolid: oklch(0.15 0.08 310);      // #1e0b4a
  --colorWorkLight: oklch(0.85 0.12 320);      // #ff88ff
  // Weitere Farben...
  
  // Spacing und andere Tokens
  --spacing: 0.25rem;
}`,
        description: "Definition von Design-Tokens mit dem OKLCH-Farbraum in TailwindCSS v4 für lebhaftere und präzisere Farbwerte."
      },
      {
        language: "scss",
        code: `@property --homegradient-color1 {
  syntax: "<color>";
  initial-value: rgba(34, 137, 158, 1);
  inherits: true;
}

@property --homegradient-color2 {
  syntax: "<color>";
  initial-value: rgba(41, 41, 186, 1);
  inherits: true;
}

.content--page__background {
  position: fixed;
  width: 100%;
  height: 100vh;
  top: 0;
  z-index: -1;

  --homegradient-color1: rgba(141, 41, 186, 1);
  --homegradient-color2: rgba(100, 012, 155, 1);
  transition: --homegradient-color1 2400ms, --homegradient-color2 400ms;

  background-image: url("pattern.svg"),
    radial-gradient(
      ellipse at -20% -10%,
      rgba(2, 0, 36, 1) 0%,
      var(--homegradient-color1) 60%,
      var(--homegradient-color2) 100%
    );
}`,
        description: "Animierte Hintergründe mit CSS @property und CSS-Variablen, die im OKLCH-Farbraum definiert sind, ermöglichen sanfte Farbübergänge zwischen verschiedenen Seitenbereichen."
      }
    ],
    images: [
      {
        src: "/assets/img/work/lighthouse_thumb.webp",
        zoomSrc: "/assets/img/work/lighthouse_metrics.webp",
        thumbSrc: "/assets/img/work/lighthouse_thumb.webp",
        alt: "Lighthouse Performance Score",
        type: "image",
        title: "Lighthouse Performance",
        caption: "Lighthouse-Bewertung mit ausgezeichneten Werten für Performance, Accessibility, Best Practices und SEO."
      },
      {
        src: "/assets/img/work/pagespeedinsights_score.webp",
        zoomSrc: "/assets/img/work/pagespeedinsights_score.webp",
        thumbSrc: "/assets/img/work/pagespeedinsights_score.webp",
        alt: "Pagespeed Insights Performance Score",
        type: "image",
        title: "Pagespeed Insights Performance",
        caption: "Pagespeed Insights Score mit ausgezeichneten Werten für Performance, Accessibility, Best Practices und SEO."
      },
      {
        src: "/assets/img/work/wave_score.webp",
        zoomSrc: "/assets/img/work/wave_score.webp",
        thumbSrc: "/assets/img/work/wave_score.webp",
        alt: "WAVE Performance Score",
        type: "image",
        title: "WAVE Performance",
        caption: "WAVE Score ohne Fehler und mit sehr guten Werten für Accessibility"
      }
    ]
  }
};

export default developmentOfRobinwichmannCom; 